import { APP_MODE_TYPES, appMode, isElectronMode } from 'src/config'

const COMMANDS = {
  STOP_SHARE_SCREEN: 'stop-stream',
  START_SHARE_SCREEN: 'start-share-screen',
  SHARE_SCREEN_DATA: 'share-screen-data',
  REQUEST_SCREEN_DATA: 'request-screen-data'
}

const CHANNEL_NAME = 'RtcChannel'

const SUPPORTED_PLAYBACK_CODEC = 'video/webm;codecs="vp8"'

const SUPPORTED_INDY_APPLICATIONS = [APP_MODE_TYPES.ADMIN, APP_MODE_TYPES.REGISTER, APP_MODE_TYPES.KIOSK]
const RECORD_INTERVAL_MS = 50

/**
 * @param {import('@quasar/app-webpack').BootFileParams} params
 */
export default async ({ app }) => {
  let currentChannel = null
  let mediaStream = null
  let mediaRecording = null

  // Only support admin (consumer), pos (producer), kiosk (producer)
  if (!_.includes(SUPPORTED_INDY_APPLICATIONS, appMode)) return

  if (!app.config.globalProperties.$cable) return

  async function startShareScreen (registerClientId) {
    const reset = !!mediaStream
    if (!reset) {
      currentChannel = app.config.globalProperties.$cable.subscriptions.create({ channel: 'RtcChannel', room: registerClientId + '_producer' }, {
        async received (data) {
          if (data.command === COMMANDS.STOP_SHARE_SCREEN) {
            stopScreenShare()
          }
        }
      })

      try {
        if (isElectronMode) {
          // Electron doesn't have access to the API so we need to call exposed function
          mediaStream = await window.captureDesktop()
        } else {
          mediaStream = await navigator.mediaDevices.getDisplayMedia({ video: true })
        }
      } catch (exception) {
        currentChannel.send({ command: COMMANDS.STOP_SHARE_SCREEN, data: {}, message: 'Error starting stream: ' + exception.message })
        currentChannel.unsubscribe()
        return
      }

      if (!mediaStream?.active) {
        currentChannel.send({ command: COMMANDS.STOP_SHARE_SCREEN, data: {}, message: 'Stream not active' })
        currentChannel.unsubscribe()
        return
      }
    } else {
      mediaRecording?.stop()
    }
    // Generate a unique id for the stream.
    // This allows us to have multiple viewers as we need to restart the stream if id changes
    const id = Math.ceil(Math.random() * 1000000)
    currentChannel.send({ command: COMMANDS.START_SHARE_SCREEN, data: {}, id })

    mediaRecording = new MediaRecorder(mediaStream, { mimeType: SUPPORTED_PLAYBACK_CODEC })
    let chunkNumber = 0
    mediaRecording.ondataavailable = (event) => {
      const chunk = chunkNumber++
      const reader = new FileReader()
      reader.readAsDataURL(event.data)
      reader.onloadend = function () {
        if (chunk > chunkNumber) return
        const base64String = reader.result
        currentChannel.send({ command: COMMANDS.SHARE_SCREEN_DATA, data: base64String, chunk, id })
      }
    }

    mediaRecording.start(RECORD_INTERVAL_MS)
  }

  function stopViewing () {
    currentChannel.send({ command: COMMANDS.STOP_SHARE_SCREEN, data: {} })
  }

  function stopScreenShare () {
    mediaRecording.stop()
    mediaStream.getTracks()[0].stop()
    mediaStream = null
    currentChannel.send({ command: COMMANDS.STOP_SHARE_SCREEN, data: {} })
  }

  function getScreenShare (videoElement, registerClientId, stopCallback = null, startCallback = null) {
    const mediaSource = new MediaSource()

    let arrayOfBlobs = []

    // Create an object URL from the `MediaSource`
    videoElement.src = URL.createObjectURL(mediaSource)

    let sourceBuffer = null
    mediaSource.addEventListener('sourceopen', function () {
      sourceBuffer = mediaSource.addSourceBuffer(SUPPORTED_PLAYBACK_CODEC)
      sourceBuffer.mode = 'sequence'

      // If we requested any video data prior to setting up the SourceBuffer,
      // we want to make sure we only append one blob at a time
      sourceBuffer.addEventListener('updateend', appendToSourceBuffer)

      // Keep track of the current sequence number in the stream currentRequestNumber
      // We use currentExecutions array to hold the data in order until we can append it to the video
      // Data has to be in order so we need to wait for the previous request to finish before processing the next one
      // This is due to how the video is encoded and how the browser decodes it, the codec requires this to be in order
      // https://developer.mozilla.org/en-US/docs/Web/API/MediaRecorder
      // https://developer.mozilla.org/en-US/docs/Web/API/Screen_Capture_API

      const currentExecutions = []
      let requested = false
      let running = false
      let count = 0
      let streamId = null
      const resolveSequence = () => {
        if (requested && !running) {
          // If requested and not running, start processing the next request
          // This is to ensure we don't have multiple requests running at the same time and append out of order
          if (!currentExecutions[streamId][count]) {
            return
          }
          const currentRequestNumber = count++
          // We have started processing the current request, so we can block the next request until we finish
          running = true
          requested = false
          currentExecutions[streamId][currentRequestNumber].then((buff) => {
            // We have finished processing the current request, so we can remove it from the array
            delete currentExecutions[streamId][currentRequestNumber]
            if (currentRequestNumber < count) arrayOfBlobs.push(buff)
            appendToSourceBuffer()
            // We have finished processing the current request, so we can unblock the next request
            running = false

            if (currentExecutions[streamId].length) {
              // We have finished processing the current request, but there are more to process
              // So we need to request the next one
              requested = true
              resolveSequence()
            }
          })
        }
      }

      currentChannel = app.config.globalProperties.$cable.subscriptions.create({ channel: CHANNEL_NAME, room: registerClientId + '_consumer' }, {
        async received (data) {
          if (data.command === COMMANDS.STOP_SHARE_SCREEN) {
            mediaSource?.endOfStream()
            currentChannel.unsubscribe()
            URL.revokeObjectURL(videoElement.src)
            videoElement.removeAttribute('src')
            videoElement.load()
            if (stopCallback) stopCallback(data.message)
          } else if (data.command === COMMANDS.START_SHARE_SCREEN) {
            currentExecutions[data.id] = {}
            streamId = data.id
            arrayOfBlobs = []
            if (startCallback) startCallback()
          } else if (data.command === COMMANDS.SHARE_SCREEN_DATA && streamId === data.id) {
            currentExecutions[data.id][data.chunk] = (new Promise((resolve, reject) => {
              return fetch(data.data).then(res => res.blob()).then(blob => {
                return resolve(blob.arrayBuffer())
              })
            }))
            requested = true
            resolveSequence()
          }
        }
      })
      currentChannel.send({ command: COMMANDS.REQUEST_SCREEN_DATA, client_id: registerClientId, data: {} })
    })

    // Add all chunks to the video
    const appendToSourceBuffer = () => {
      if (mediaSource.readyState === 'open' &&
        sourceBuffer &&
        sourceBuffer.updating === false &&
        arrayOfBlobs.length > 0
      ) {
        sourceBuffer.appendBuffer(arrayOfBlobs.shift())
      }

      // Limit the total buffer size to 20 minutes
      // This way we don't run out of memory
      if (videoElement.buffered.length &&
        videoElement.buffered.end(0) - videoElement.buffered.start(0) > 1200
      ) {
        sourceBuffer.remove(0, videoElement.buffered.end(0) - 1200)
      }
    }
  }

  app.config.globalProperties.$startShareScreen = startShareScreen
  app.config.globalProperties.$stopShareScreen = stopScreenShare
  app.config.globalProperties.$getScreenShare = getScreenShare
  app.config.globalProperties.$stopViewing = stopViewing
}
